import { useGetUserCustomSkillsQuery, useGetUserSkillsQuery } from '@/services/ApiService';
import { useMemo } from 'react';
import { UserCustomSkillModel, UserSkillModel } from '@/apiTypes/UserTypes.ts';
import { SkillEnum } from '@/apiTypes/SkillsTypes.ts';

// Stable identities so consumers memoizing on `skills`/`customSkills` aren't invalidated
// every render while the queries are still undefined.
const EMPTY_USER_SKILLS: UserSkillModel[] = [];
const EMPTY_CUSTOM_SKILLS: UserCustomSkillModel[] = [];

export const useUserSkills = (options?: { skip?: boolean }) => {
    const skip = options?.skip ?? false;
    const {
        data: skills,
        isFetching: isFetchingUserSkills,
        isLoading: isLoadingUserSkills,
        isUninitialized: isUninitializedUserSkills,
        isError: isUserSkillsError,
    } = useGetUserSkillsQuery(undefined, { skip });

    const {
        data: customSkills,
        isFetching: isFetchingUserCustomSkills,
        isLoading: isLoadingUserCustomSkills,
        isUninitialized: isUninitializedUserCustomSkills,
        isError: isCustomUserSkillsError,
    } = useGetUserCustomSkillsQuery(undefined, { skip });

    const isFetching = useMemo(() => {
        return isFetchingUserSkills || isFetchingUserCustomSkills;
    }, [isFetchingUserSkills, isFetchingUserCustomSkills]);

    // The first load only, where `isFetching` is also true on every later refetch. A UI that
    // swaps the content out on this one keeps it up while a refetch runs underneath.
    const isLoading = useMemo(() => {
        return isLoadingUserSkills || isLoadingUserCustomSkills;
    }, [isLoadingUserSkills, isLoadingUserCustomSkills]);

    /*
     * Stays true forever when `skip` is set, since a skipped query never initialises. Treat it as
     * "loading" only when the caller does not skip, or the UI waits on something that never comes.
     */
    const isUninitialized = useMemo(() => {
        return isUninitializedUserSkills || isUninitializedUserCustomSkills;
    }, [isUninitializedUserSkills, isUninitializedUserCustomSkills]);

    const isError = useMemo(() => {
        return isUserSkillsError || isCustomUserSkillsError;
    }, [isUserSkillsError, isCustomUserSkillsError]);

    const { powerSkills, specialtySkills, certifications } = useMemo(() => {
        const allSkills = skills ?? EMPTY_USER_SKILLS;

        return allSkills.reduce<{
            powerSkills: UserSkillModel[];
            specialtySkills: UserSkillModel[];
            certifications: UserSkillModel[];
        }>(
            (acc, skill) => {
                if (skill.skillType === SkillEnum.COMMON) {
                    acc.powerSkills.push(skill);
                }
                else if (skill.skillType === SkillEnum.SPECIALIZED) {
                    acc.specialtySkills.push(skill);
                }
                else if (skill.skillType === SkillEnum.CERTIFICATION) {
                    acc.certifications.push(skill);
                }
                return acc;
            },
            { powerSkills: [], specialtySkills: [], certifications: [] },
        );
    }, [skills]);

    return {
        skills: skills ?? EMPTY_USER_SKILLS,
        powerSkills,
        specialtySkills,
        certifications,
        customSkills: customSkills ?? EMPTY_CUSTOM_SKILLS,
        isFetching,
        isFetchingUserSkills,
        isLoading,
        isUninitialized,
        isError,
    };
};
